import { useState } from "react";
import { createListStore, getFilterDto, getSort } from "../../common/listStore";
import { PageTitle } from "../../layout/PageTitle";
import { listHeaderItems, renderListStore } from "../../common/listStoreUi";
import { Button, Grid } from "@mui/material";
import { createEntityStore } from "../../common/entityStore";
import { today } from "../../common/utils";
import { propValidValue } from "../../common/entityStoreValidators";
import { propDateField, propSimpleEnumField } from "../../common/entityStoreForm";
import { downloadFromApiPost } from "../../common/download";
import { nextDay, applyListStoreFilters } from "../../common/listStoreFilters";
import { RetentionCodeId, orderTypes } from "../../common/enum";

export interface CustomerRetentionDto {
    id: number;
    cancelReasonId: number;
    cancelReasonName: string;
    retentionCodeId: RetentionCodeId;
    retentionCodeName: string;
    retentionDate: string;
    custId: string;
    ordId: number;
    productCode: string;
    projectCode: string;
    orderSc: string;
    ordTyp: string;
    customerStateName: string;
    orderStateName: string;
    totalPaid: number;
    userName: string;
}

interface ListFilters {
    from?: string;
    to?: string;
    fromFormatted?: string;
    toFormatted?: string;
    ordTyp?: string;
}

function getFiltersStoreInitialData() {
    return { from: today(), to: today() };
}

export function RetentionReportPage() {
    const [{ useStore, useFiltersStore }] = useState(() => {
        const useStore = createListStore<CustomerRetentionDto>({
            path: "reports/customerRetentions/list",
            supportsApiFilterSortPage: true,
            itemsPerPage: 15,
            listProps: [
                { column: 'id', label: 'ID', width: 90, type: "number" },
                { column: 'cancelReasonId', label: 'Reason ID', width: 100, type: "number" },
                { column: 'cancelReasonName', label: 'Cancellation Reason', width: 210 },
                { column: 'retentionCodeId', label: 'Retention ID', width: 140, type: "number" },
                { column: 'retentionCodeName', label: 'Retention Code', width: 180 },
                { column: 'retentionDate', label: 'Retention Date', type: 'date', width: 120 },
                { column: 'custId', label: 'Customer', width: 120 },
                { column: 'ordId', label: 'Order', width: 100, type: "number" },
                { column: 'productCode', label: 'Product', width: 100 },
                { column: 'projectCode', label: 'Project', width: 100 },
                { column: 'orderSc', label: 'MC', width: 50 },
                { column: 'customerStateName', label: 'Customer Status', width: 150 },
                { column: 'orderStateName', label: 'Order Status', width: 120 },
                { column: 'totalPaid', label: 'Total Paid', width: 110, type: "currency" },
                { column: 'userName', label: 'User', width: 110 },
            ]
        });
        const useFiltersStore = createEntityStore<ListFilters>({
            data: getFiltersStoreInitialData(), isValidationEnabled: true,
            propValidators: [
                propValidValue("from", "fromFormatted"),
                propValidValue("to", "toFormatted"),
            ]
        });
        applyListStoreFilters(useFiltersStore, useStore, [
            { listProps: ["retentionDate"], operator: "GreaterThanOrEqual", filterProp: "from" },
            { listProps: ["retentionDate"], operator: "LessThan", filterProp: "to", transform: nextDay },
            { listProps: ["ordTyp"], operator: "Equal", filterProp: "ordTyp" },
        ]);
        return { useStore, useFiltersStore };
    });

    function clearFiltersStore() {
        useFiltersStore.getState().setClientData(getFiltersStoreInitialData());
    }

    const { isFetching } = useStore(p => ({ isFetching: p.fetching }));

    return <>
        <PageTitle title="Retention report" />
        {renderListStore(useStore, {
            header: listHeaderItems([
                ["export", () => <Button disabled={isFetching} onClick={() => downloadFromApiPost(`reports/customerRetentions/xlsx`, {
                    filter: getFilterDto(useStore.getState()),
                    sort: getSort(useStore.getState()),
                })}>Export</Button>],
            ]),
            filtersMaxWidth: "md",
            renderExternalFilters: () => <>
                <Grid item xs={4} md={3}>{propDateField(useFiltersStore)("from", "fromFormatted", "Date from")}</Grid>
                <Grid item xs={4} md={3}>{propDateField(useFiltersStore)("to", "toFormatted", "Date to")}</Grid>
                <Grid item xs={4} md={3}>{propSimpleEnumField(useFiltersStore)("ordTyp", "Order type", orderTypes, { addAll: true })}</Grid>
            </>, clearExternalFilters: clearFiltersStore,
        })};
    </>
}
